import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getApiUrl, SERVERS, ENDPOINTS } from '../config/api'
import { 
  faSearch, 
  faFilePdf, 
  faChartBar,
  faClock,
  faGraduationCap,
  faStar,
  faExclamationTriangle,
  faCheckCircle,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faDownload,
  faEye,
  faBrain,
  faBullseye,
  faLightbulb,
  faTimes,
  faFileAlt,
  faImage,
  faSyncAlt,
  faAward,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons'
import './AnalyzeQuestionPapers.css'

const JustificationWithToggle = ({ text, charLimit = 180 }) => {
  const [expanded, setExpanded] = useState(false)
  if (!text) return <span>No justification available</span>
  if (text.length <= charLimit) return <span>{text}</span>
  return (
    <span>
      {expanded ? text : text.slice(0, charLimit) + '...'}
      <button
        className="show-more-btn"
        style={{ marginLeft: 8, fontSize: '0.9em', background: 'none', border: 'none', color: '#ffd700', cursor: 'pointer', textDecoration: 'underline' }}
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? 'Show less' : 'Show more'}
      </button>
    </span>
  )
}

const AnalyzeQuestionPapers = () => {
  const [formData, setFormData] = useState({
    courseId: '',
    courseName: '',  // For display only
    instructorName: '',
    academicYear: '',
    semester: '',
    rubricName: 'Generic Rubric',
    paperRubricName: ''  // Optional paper level rubric
  })
  
  const [questionPapers, setQuestionPapers] = useState([])
  const [selectedPaper, setSelectedPaper] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisResults, setAnalysisResults] = useState(null)
  const [expandedQuestions, setExpandedQuestions] = useState({})
  const [error, setError] = useState('')
  const [questionPdfUrls, setQuestionPdfUrls] = useState({})
  const [loadingPdfs, setLoadingPdfs] = useState({})
  const [failedPdfs, setFailedPdfs] = useState({})  // Track failed PDF loads
  const [cacheInfo, setCacheInfo] = useState(null)
  const [rubrics, setRubrics] = useState([])
  const [paperRubrics, setPaperRubrics] = useState([])  // Separate list for paper level rubrics
  const [handoutWarning, setHandoutWarning] = useState('')
    const [uploadResults, setUploadResults] = useState(null)
    const [samlData, setSamlData] = useState({});
    const [userData, setUserData] = useState({});
  // Academic years for dropdown (last 10 years)
  const academicYears = []
  const currentYear = new Date().getFullYear()
  for (let i = 0; i < 10; i++) {
    const year = currentYear - i
    academicYears.push(`${year}-${(year + 1).toString().slice(-2)}`)
  }

  // Fetch rubrics on component mount
  useEffect(() => {
    const fetchRubrics = async () => {
      try {
        // Fetch question level rubrics
        const questionResponse = await fetch(getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRICS) + '?valid_only=true&rubric_type=question')
        const questionData = await questionResponse.json()
        if (questionData.success) {
          setRubrics(questionData.data)
        }
        
        // Fetch paper level rubrics
        const paperResponse = await fetch(getApiUrl(SERVERS.DATABASE, ENDPOINTS.RUBRICS) + '?valid_only=true&rubric_type=paper')
        const paperData = await paperResponse.json()
        if (paperData.success) {
          setPaperRubrics(paperData.data)
        }
      } catch (err) {
        console.error('Error fetching rubrics:', err)
      }
    }
    fetchRubrics()
  }, [])


  useEffect(() => {
  const fetchSamlData = async () => {
    try {
      // IMPORTANT: use exact backend path (qpra not qbg)
      const url = `/qpra/get-saml-data`;
      console.log("[SAML] Fetching SAML data from:", url);

      const response = await fetch(url, {
        method: "GET",
        credentials: "include", // include HttpOnly cookie
        headers: {
          "Accept": "application/json",
        },
      });

      if (!response.ok) {
        console.warn("[SAML] fetch returned non-ok:", response.status);
        // If unauthorized, redirect to login (start SAML flow)
        if (response.status === 401) {
          console.log("[SAML] Session missing/expired — redirecting to login");
          window.location.href = `/qprs/login`;
          return;
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setUserData(data);
      // Defensive extraction
      console.log("data",data)
      const courseData = {};
      const apiData = data?.api_data;

      if (Array.isArray(apiData)) {
        apiData.forEach((source, srcIndex) => {
          const ds = source?.["data-source"] || source?.data_source || `source_${srcIndex}`;
          const courses = Array.isArray(source?.courses) ? source.courses : Array.isArray(source?.data) ? source.data : [];

          courses.forEach((course) => {
            if (!course || typeof course !== "object") return;

            if (ds === "taxila") {
              // Taxila: key = fullname, value = shortname
              if (course.fullname && course.shortname) {
                courseData[course.fullname] = course.shortname;
              }
            } else if (ds === "canvas") {
              // Canvas: key = name, value = course_code
              if (course.name && course.course_code) {
                courseData[course.name] = course.course_code;
              }
            } else {
              // generic fallback — try common fields
              const title = course.fullname || course.name || course.title;
              const code = course.shortname || course.course_code || course.code || course.id;
              if (title && code) courseData[title] = code;
            }
          });
        });
      } else {
        console.warn("[SAML] api_data missing or not an array:", apiData);
      }

      setSamlData(courseData);
      setError(null);
    } catch (err) {
      console.error("Error fetching SAML data:", err);
      setError(err.message || "Unknown error");
      setSamlData({}); // ensure consistent state shape
    }
  };

  fetchSamlData();
}, []);

const handleInputChange = (e) => {
  const { name, value } = e.target;

  // Special case for courseId dropdown - find corresponding course name
  if (name === "courseId") {
    // Find the course name for this code
    const courseName = Object.keys(samlData).find(name => samlData[name] === value) || "";
    setFormData((prev) => ({
      ...prev,
      courseId: value,           // course_code (primary)
      courseName: courseName,    // course_name (for display)
    }));
  } else {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  }


    
    // Reset dependent fields when course/year/semester changes
    if (['courseId', 'academicYear', 'semester'].includes(name)) {
      setSelectedPaper('')
      setQuestionPapers([])
      setAnalysisResults(null)
    }
  }

  const validateForm = () => {
    if (!formData.courseId.trim()) {
      setError('Course Code is required')
      return false
    }
    if (!formData.academicYear) {
      setError('Academic Year is required')
      return false
    }
    if (!formData.semester) {
      setError('Semester is required')
      return false
    }
    return true
  }

  const checkHandoutExists = async (courseId, courseTitle, academicYear, semester) => {
    try {
      const params = new URLSearchParams()
      if (courseId) params.append('course_id', courseId)
      if (courseTitle) params.append('course_title', courseTitle)
      params.append('academic_year', academicYear)
      params.append('semester', semester)
      
      const response = await fetch(`/postgres/handouts/check?${params}`)
      const result = await response.json()
      return result.exists || false
    } catch (error) {
      console.error('Error checking handout:', error)
      return false
    }
  }

  const fetchQuestionPapers = async () => {
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const params = new URLSearchParams({
        course_id: formData.courseId,
        course_title: formData.courseName,
        academic_year: formData.academicYear,
        semester: formData.semester
      })

      const response = await fetch(getApiUrl(SERVERS.DATABASE, ENDPOINTS.QUESTION_PAPERS) + `?${params}`)
      const result = await response.json()

      if (result.success) {
        setQuestionPapers(result.data)
        if (result.data.length === 0) {
          setError('No question papers found for the selected criteria')
        }
      } else {
        setError('Failed to fetch question papers')
      }
    } catch (error) {
      console.error('Error fetching question papers:', error)
      setError('Failed to fetch question papers. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAnalyze = async () => {
    if (!selectedPaper) {
      setError('Please select a question paper to analyze')
      return
    }

    // Check if handout exists for this course
    setHandoutWarning('')
    const handoutExists = await checkHandoutExists(formData.courseId, null, formData.academicYear, formData.semester)
    if (!handoutExists) {
      setHandoutWarning('⚠️ No course handout found for this course. Paper-level analysis may be less accurate without handout context.')
    }

    setIsAnalyzing(true)
    setError('')
    setAnalysisResults(null)
    // Clear previous PDF URLs
    setQuestionPdfUrls({})

    try {
      const response = await fetch(`/analysis/analyze-paper-concurrent/${selectedPaper}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question_rubric_name: formData.rubricName,
          paper_rubric_name: formData.paperRubricName || null
        })
      })

      const result = await response.json()

      if (result.success) {
        console.log('Analysis results received:', result.data)
        console.log('Questions analysis:', result.data.questions_analysis)
        console.log('Cache info:', result.cache_info)
        
        setAnalysisResults(result.data)
        setCacheInfo(result.cache_info)
        
        // Preload first few question PDFs for better UX with staggered loading
        if (result.data.questions_analysis && result.data.questions_analysis.length > 0) {
          const firstThreeQuestions = result.data.questions_analysis.slice(0, 3)
          console.log('First three questions:', firstThreeQuestions)
          firstThreeQuestions.forEach((question, index) => {
            console.log('Question data:', question)
            if (question.question_id) {
              console.log('Loading PDF for question_id:', question.question_id)
              // Add small delay for first few questions to avoid race conditions
              setTimeout(() => {
                loadQuestionPdf(question.question_id)
              }, index * 500) // 500ms delay between each question
            } else {
              console.log('No question_id found for question:', question)
            }
          })
        }
      } else {
        setError('Failed to analyze question paper')
      }
    } catch (error) {
      console.error('Error analyzing question paper:', error)
      setError('Failed to analyze question paper. Please try again.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  const loadQuestionPdf = async (questionId, forceRefresh = false) => {
    if (questionPdfUrls[questionId] && !forceRefresh) {
      return // Already loaded
    }

    console.log(`Loading PDF for question: ${questionId}${forceRefresh ? ' (forced refresh)' : ''}`)
    setLoadingPdfs(prev => ({ ...prev, [questionId]: true }))
    setFailedPdfs(prev => ({ ...prev, [questionId]: false })) // Reset failed state
    
    try {
      // Add cache-busting parameter to force refresh
      const cacheBuster = forceRefresh ? `?t=${Date.now()}` : ''
      const response = await fetch(`/analysis/question-pdf/${questionId}${cacheBuster}`)
      console.log(`PDF response status: ${response.status}`)
      
      if (response.ok) {
        const blob = await response.blob()
        console.log(`PDF blob size: ${blob.size} bytes`)
        
        // Check if blob is valid
        if (blob.size === 0) {
          throw new Error('PDF blob is empty')
        }
        
        // Revoke old URL if refreshing
        if (forceRefresh && questionPdfUrls[questionId]) {
          URL.revokeObjectURL(questionPdfUrls[questionId])
        }
        
        const url = URL.createObjectURL(blob)
        setQuestionPdfUrls(prev => ({ ...prev, [questionId]: url }))
        console.log(`PDF loaded successfully for question ${questionId}`)
      } else {
        const errorText = await response.text()
        console.error(`Failed to load PDF for question ${questionId}:`, response.status, errorText)
        setFailedPdfs(prev => ({ ...prev, [questionId]: true }))
      }
    } catch (error) {
      console.error(`Error loading PDF for question ${questionId}:`, error)
      setFailedPdfs(prev => ({ ...prev, [questionId]: true }))
    } finally {
      setLoadingPdfs(prev => ({ ...prev, [questionId]: false }))
    }
  }

  const toggleQuestionExpansion = async (questionNumber, questionId) => {
    const isExpanding = !expandedQuestions[questionNumber]
    
    setExpandedQuestions(prev => ({
      ...prev,
      [questionNumber]: isExpanding
    }))
    
    // Load PDF when expanding
    if (isExpanding) {
      await loadQuestionPdf(questionId)
    }
  }

  const refreshQuestionPdf = async (questionId) => {
    console.log(`Refreshing PDF for question: ${questionId}`)
    await loadQuestionPdf(questionId, true) // Force refresh
  }

  // Cleanup PDF URLs when component unmounts or analysis results change
  useEffect(() => {
    return () => {
      // Cleanup PDF URLs to prevent memory leaks
      Object.values(questionPdfUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url)
      })
    }
  }, [questionPdfUrls])

  const getBloomLevelName = (level) => {
    const levels = {
      1: 'Remember',
      2: 'Understand', 
      3: 'Apply',
      4: 'Analyze',
      5: 'Evaluate',
      6: 'Create'
    }
    return levels[level] || 'Unknown'
  }

  const getQualityScoreColor = (score) => {
    if (score >= 8) return '#10B981' // Green
    if (score >= 6) return '#F59E0B' // Yellow
    return '#EF4444' // Red
  }

  const getTimeStatusColor = (status) => {
    switch (status) {
      case 'Adequate': return '#10B981'
      case 'Too Little': return '#EF4444'
      case 'Too Much': return '#F59E0B'
      default: return '#6B7280'
    }
  }

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return `${hours}h ${mins}m`
    }
    return `${mins}m`
  }

  return (
    <div className="analyze-container">
      <div className="container">
        <div className="analyze-header">
          <div className="header-icon">
            <FontAwesomeIcon icon={faSearch} />
          </div>
          <div className="header-text">
            <h1>Analyze Question Papers</h1>
            <p>Comprehensive analysis of question papers with AI-powered insights</p>
          </div>
        </div>

      {/* Selection Form */}
      <div className="selection-form">
        <div className="form-grid-5">
          <div className="form-group">
  <label htmlFor="courseId" className="required">Course Code</label>
  <div className="dropdown-container">
    <div className="select-wrapper">
      <select
        id="courseId"
        name="courseId"
        value={formData.courseId}
        onChange={handleInputChange}
        className="input-field"
        required
      >
        <option value="" disabled>Select a Course Code</option>
        {Object.entries(samlData)
          .sort((a, b) => a[1].localeCompare(b[1]))  // Sort by course code
          .map(([courseName, courseCode], index) => (
          <option key={index} value={courseCode}>
            {courseCode} - {courseName}
          </option>
        ))}
      </select>
      <div className="select-arrow">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
        </svg>
      </div>
    </div>
  </div>
</div>
       <div className="form-group">
  <label htmlFor="courseName">Course Name</label>
  <input
    type="text"
    id="courseName"
    name="courseName"
    value={formData.courseName}
    className="input-field"
    readOnly
    placeholder="Course Name will appear here"
  />
</div>
</div>
<div className="form-grid-6">
   

          <div className="form-group">
            <label htmlFor="semester">Semester *</label>
            <select
              id="semester"
              name="semester"
              value={formData.semester}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Semester</option>
              <option value="1">Semester 1</option>
              <option value="2">Semester 2</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="rubricName">Question Level Assessment Rubric *</label>
            <select
              id="rubricName"
              name="rubricName"
              value={formData.rubricName}
              onChange={handleInputChange}
              required
            >
              {rubrics.map(rubric => (
                <option key={rubric.id} value={rubric.name}>
                  {rubric.name} {rubric.is_generic ? '(Default)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="paperRubricName">Paper Level Assessment Rubric</label>
            <select
              id="paperRubricName"
              name="paperRubricName"
              value={formData.paperRubricName}
              onChange={handleInputChange}
            >
              <option value="">Select a paper level rubric</option>
              {paperRubrics.map(rubric => (
                <option key={rubric.id} value={rubric.name}>
                  {rubric.name} {rubric.is_generic ? '(Default)' : ''}
                </option>
              ))}
            </select>
          </div>

                 <div className="form-group">
            <label htmlFor="academicYear">Academic Year *</label>
            <select
              id="academicYear"
              name="academicYear"
              value={formData.academicYear}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Academic Year</option>
              {academicYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button 
            className="btn btn-primary"
            onClick={fetchQuestionPapers}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Loading...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSearch} />
                Find Question Papers
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="error-message">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            {error}
          </div>
        )}
      </div>

      {/* Question Papers List */}
      {questionPapers.length > 0 && (
        <div className="papers-section">
          <h2>
            <FontAwesomeIcon icon={faFilePdf} />
            Available Question Papers
          </h2>
          
          <div className="papers-grid">
            {questionPapers.map(paper => (
              <div 
                key={paper.id}
                className={`paper-card ${selectedPaper === paper.id ? 'selected' : ''}`}
                onClick={() => setSelectedPaper(paper.id)}
              >
                <div className="paper-header" title={paper.filename || paper.original_filename}>
                  <FontAwesomeIcon icon={faFilePdf} className="paper-icon" />
                  <h3>{paper.filename || paper.original_filename}</h3>
                </div>
                
                <div className="paper-details">
                  <p><strong>Course:</strong> {paper.course_name || paper.course_id}</p>
                  <p><strong>Instructor:</strong> {paper.instructor_name || 'Not specified'}</p>
                  <p><strong>Questions:</strong> {paper.question_count}</p>
                  <p><strong>Total Marks:</strong> {paper.total_marks}</p>
                  <p><strong>Program Category:</strong> {paper.program_category}</p>
                  <p><strong>Program Committee:</strong> {paper.program_committee}</p>
                  <p><strong>Exam Type:</strong> {paper.exam_type}</p>
                  <p><strong>Duration:</strong> {formatTime(paper.total_duration_minutes || 180)}</p>
                </div>

                {selectedPaper === paper.id && (
                  <div className="selection-indicator">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Selected
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Handout Warning */}
          {handoutWarning && (
            <div className="handout-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{handoutWarning}</span>
            </div>
          )}

          <div className="analyze-action">
            <button 
              className="btn btn-analyze"
              onClick={handleAnalyze}
              disabled={!selectedPaper || isAnalyzing}
            >
              {isAnalyzing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Analyzing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faBrain} />
                  Analyze Question Paper
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysisResults && (
        <div className="analysis-results">
          <div className="results-header">
            <h2>
              <FontAwesomeIcon icon={faChartBar} />
              Analysis Results
            </h2>
            <div className="paper-info">
              <h3>{analysisResults.original_filename || analysisResults.paper_metadata?.original_filename}</h3>
              <p>{analysisResults.paper_metadata.course_name} • {analysisResults.paper_metadata.instructor_name}</p>
              {cacheInfo && (
                <div className={`cache-indicator ${cacheInfo.source === 'database_cache' ? 'cached' : 'live'}`}>
                  <FontAwesomeIcon icon={cacheInfo.source === 'database_cache' ? faDownload : faBrain} />
                  {cacheInfo.message}
                </div>
              )}
            </div>
          </div>

          {/* Overall Metrics */}
          <div className="overall-metrics">
            <div className="metric-card">
              <div className="metric-icon">
                <FontAwesomeIcon icon={faStar} />
              </div>
              <div className="metric-content">
                <h4>Overall Quality</h4>
                <div className="metric-value" style={{ color: getQualityScoreColor(analysisResults.overall_metrics.average_quality_score) }}>
                  {analysisResults.overall_metrics.average_quality_score}/10
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">
                <FontAwesomeIcon icon={faGraduationCap} />
              </div>
              <div className="metric-content">
                <h4>Difficulty Level</h4>
                <div className="metric-value">
                  {analysisResults.overall_metrics.weighted_difficulty.toFixed(1)}/6
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="metric-content">
                <h4>
                  Time Allocation Ratio{" "}
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    style={{ 
                      fontSize: '0.8em', 
                      color: '#6c757d', 
                      cursor: 'help' 
                    }}
                    title="Ratio of allocated exam time to estimated completion time. Values >100% indicate sufficient time allocation."
                  />
                </h4>
                <div className="metric-value" style={{ color: getTimeStatusColor(analysisResults.overall_metrics.time_analysis.time_status) }}>
                  {analysisResults.overall_metrics.time_analysis.time_efficiency_percentage}%
                </div>
                <small>{analysisResults.overall_metrics.time_analysis.time_status}</small>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">
                <FontAwesomeIcon icon={faBullseye} />
              </div>
              <div className="metric-content">
                <h4>Questions</h4>
                <div className="metric-value">
                  {analysisResults.overall_metrics.total_questions}
                </div>
                <small>{analysisResults.overall_metrics.total_marks} total marks</small>
              </div>
            </div>
          </div>

          {/* Bloom's Distribution */}
          <div className="blooms-distribution">
            <h3>Bloom's Taxonomy Distribution</h3>
            <div className="blooms-chart">
              {Object.entries(analysisResults.overall_metrics.blooms_distribution).map(([level, count]) => (
                <div key={level} className="bloom-bar">
                  <div className="bloom-label">
                    {level}: {getBloomLevelName(parseInt(level))}
                  </div>
                  <div className="bloom-bar-container">
                    <div 
                      className="bloom-bar-fill"
                      style={{ 
                        width: `${(count / analysisResults.overall_metrics.total_questions) * 100}%` 
                      }}
                    >
                      {count}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {analysisResults.recommendations.length > 0 && (
            <div className="recommendations">
              <h3>
                <FontAwesomeIcon icon={faLightbulb} />
                Recommendations
              </h3>
              <div className="recommendations-list">
                {analysisResults.recommendations.map((rec, index) => (
                  <div key={index} className="recommendation-item">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {rec}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Final Evaluation Framework */}
          {analysisResults.score_breakdown && (
            <div className="final-evaluation-framework">
              <h3>
                <FontAwesomeIcon icon={faAward} />
                Final Evaluation Framework
              </h3>
              <div className="evaluation-card">
                <div className="evaluation-header">
                  <div className="final-score-display">
                    <div className="score-circle">
                      <span className="score-number">{analysisResults.final_weighted_score?.toFixed(1) || '0.0'}</span>
                      <span className="score-label">Final Score</span>
                    </div>
                    <div className="rating-info">
                      <div className="rating-badge">{analysisResults.final_rating || 'N/A'}</div>
                      <div className="recommendation-text">{analysisResults.final_recommendation || 'No recommendation available'}</div>
                    </div>
                  </div>
                </div>
                
                <div className="score-breakdown">
                  <h4>Score Breakdown</h4>
                  <div className="breakdown-grid">
                    <div className="breakdown-item">
                      <div className="breakdown-header">
                        <span className="breakdown-label">Individual Questions</span>
                        <span className="breakdown-weight">({(analysisResults.score_breakdown.question_level.weight * 100).toFixed(0)}%)</span>
                      </div>
                      <div className="breakdown-scores">
                        <span className="raw-score">{analysisResults.score_breakdown.question_level.raw_score}</span>
                        <span className="arrow">→</span>
                        <span className="weighted-score">{analysisResults.score_breakdown.question_level.weighted_score}</span>
                      </div>
                    </div>
                    
                    {analysisResults.score_breakdown.paper_level.weight > 0 && (
                      <div className="breakdown-item">
                        <div className="breakdown-header">
                          <span className="breakdown-label">Overall Paper Criteria</span>
                          <span className="breakdown-weight">({(analysisResults.score_breakdown.paper_level.weight * 100).toFixed(0)}%)</span>
                        </div>
                        <div className="breakdown-scores">
                          <span className="raw-score">{analysisResults.score_breakdown.paper_level.raw_score}</span>
                          <span className="arrow">→</span>
                          <span className="weighted-score">{analysisResults.score_breakdown.paper_level.weighted_score}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="breakdown-summary">
                    <div className="summary-item">
                      <span className="summary-label">Total Questions Analyzed:</span>
                      <span className="summary-value">{analysisResults.score_breakdown.total_questions}</span>
                    </div>
                    {analysisResults.score_breakdown.incomplete_questions > 0 && (
                      <div className="summary-item incomplete">
                        <span className="summary-label">Incomplete Questions:</span>
                        <span className="summary-value">{analysisResults.score_breakdown.incomplete_questions}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Paper Level Analysis */}
          {analysisResults.paper_level_analysis && (
            <div className="paper-level-analysis">
              <h3>
                <FontAwesomeIcon icon={faFileAlt} />
                Paper Level Analysis
              </h3>
              <div className="paper-analysis-card">
                  <div className="paper-analysis-header">
                    <h4>Overall Paper Assessment</h4>
                  </div>
                
                <div className="paper-rubric-assessment">
                  <h5>Paper Level Criteria Assessment:</h5>
                  <div className="paper-criteria-grid">
                    {Object.entries(analysisResults.paper_level_analysis.rubric_assessment).map(([criterion, assessment]) => (
                      <div key={criterion} className="paper-criterion-item">
                        <div className="criterion-header">
                          <h6>{criterion}</h6>
                          <span className="criterion-score">{assessment.score}</span>
                        </div>
                        <div className="criterion-justification">
                          <p>{assessment.justification}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Questions Analysis */}
          <div className="questions-analysis">
            <h3>Question-wise Analysis</h3>
            <div className="questions-list">
              {analysisResults.questions_analysis.map((question) => (
                <div key={question.question_number} className="question-card">
                  <div 
                    className="question-header"
                    onClick={() => toggleQuestionExpansion(question.question_number, question.question_id)}
                  >
                    <div className="question-info">
                      <h4>Question {question.question_number}</h4>
                      <div className="question-metrics">
                        <span className="metric">
                          <FontAwesomeIcon icon={faStar} />
                          Quality: {question.quality_score}/10
                        </span>
                        <span className="metric">
                          <FontAwesomeIcon icon={faGraduationCap} />
                          Bloom: {question.blooms_level} ({getBloomLevelName(question.blooms_level)})
                        </span>
                        <span className="metric">
                          <FontAwesomeIcon icon={faClock} />
                          Time: {formatTime(question.estimated_time_minutes)}
                        </span>
                        <span className="metric">
                          Marks: {question.marks}
                        </span>
                      </div>
                    </div>
                    <div className="expand-icon">
                      <FontAwesomeIcon 
                        icon={expandedQuestions[question.question_number] ? faChevronUp : faChevronDown} 
                      />
                    </div>
                  </div>

                  {expandedQuestions[question.question_number] && (
                    <div className="question-details">
                      <div className="question-pdf-viewer">
                        <h5>
                          <FontAwesomeIcon icon={faFilePdf} />
                          Question PDF:
                        </h5>
                        {loadingPdfs[question.question_id] ? (
                          <div className="pdf-loading">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Loading PDF...
                          </div>
                        ) : questionPdfUrls[question.question_id] ? (
                          <div className="pdf-container">
                            <object
                              data={questionPdfUrls[question.question_id]}
                              type="application/pdf"
                              width="100%"
                              height="500px"
                              style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                            >
                              <div style={{ padding: '20px', textAlign: 'center' }}>
                                <p style={{ marginBottom: '10px' }}>Failed to load PDF</p>
                                <p style={{ marginBottom: '15px' }}>Please use the "Refresh PDF" button or download the PDF to view it.</p>
                              </div>
                            </object>
                            <div className="pdf-actions">
                              <button
                                onClick={() => refreshQuestionPdf(question.question_id)}
                                className="btn btn-primary btn-sm"
                                disabled={loadingPdfs[question.question_id]}
                              >
                                <FontAwesomeIcon icon={faSyncAlt} spin={loadingPdfs[question.question_id]} />
                                Refresh PDF
                              </button>
                              <a 
                                href={questionPdfUrls[question.question_id]} 
                                download={`question_${question.question_number}.pdf`}
                                className="btn btn-secondary btn-sm"
                              >
                                <FontAwesomeIcon icon={faDownload} />
                                Download PDF
                              </a>
                            </div>
                          </div>
                        ) : (
                          <div className="pdf-error">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <div>Failed to load PDF for Question {question.question_number}</div>
                            <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                              Question ID: {question.question_id}
                            </div>
                            <div className="pdf-error-actions">
                              <button
                                onClick={() => refreshQuestionPdf(question.question_id)}
                                className="btn btn-primary btn-sm"
                                disabled={loadingPdfs[question.question_id]}
                              >
                                <FontAwesomeIcon icon={faSyncAlt} spin={loadingPdfs[question.question_id]} />
                                Retry Loading PDF
                              </button>
                            </div>
                            <div className="question-text-fallback">
                              <p>{question.question_text ? question.question_text.substring(0, 300) + '...' : 'Question text not available'}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="rubric-scores">
                        <h5>Quality Assessment:</h5>
                        <div className="scores-grid">
                          {Object.entries(question.rubric_assessment).map(([key, value]) => {
                            if (key.endsWith('_justification')) return null

                            // Handle different value types safely
                            let score = 0
                            let displayValue = value
                            let justification = 'No justification available'

                            if (typeof value === 'object' && value !== null) {
                              if ('score' in value) {
                                score = parseInt(value.score.split('/')[0]) || 0
                                displayValue = value.score
                              }
                              if ('justification' in value && value.justification) {
                                justification = value.justification
                              }
                            } else if (typeof value === 'string' && value.includes('/')) {
                              score = parseInt(value.split('/')[0]) || 0
                              displayValue = value
                            } else if (typeof value === 'number') {
                              score = value
                              displayValue = `${value}/3`
                            } else {
                              displayValue = 'N/A'
                            }
                            
                            return (
                              <div key={key} className="score-item">
                                <div className="score-header">
                                  <span className="score-label">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                                  <span className="score-value">{displayValue}</span>
                                </div>
                                <p className="score-justification">
                                  <JustificationWithToggle text={justification} />
                                </p>
                              </div>
                            )
                          })}
                        </div>
                      </div>

                      <div className="strengths-weaknesses">
                        <div className="strengths">
                          <h5>Strengths:</h5>
                          <ul>
                            {Array.isArray(question.strengths) ? question.strengths.map((strength, index) => (
                              <li key={index}>{strength}</li>
                            )) : <li>No strengths identified</li>}
                          </ul>
                        </div>
                        <div className="weaknesses">
                          <h5>Areas for Improvement:</h5>
                          <ul>
                            {Array.isArray(question.weaknesses) ? question.weaknesses.map((weakness, index) => (
                              <li key={index}>{weakness}</li>
                            )) : <li>No areas for improvement identified</li>}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default AnalyzeQuestionPapers 